import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Copy, X, AlertCircle, Clock, Zap } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AIResponse } from '@/types/debug';
import { useSimpleToast } from '@/hooks/use-simple-toast';

interface ResponseViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  response: AIResponse | null;
}

export function ResponseViewModal({ 
  isOpen, 
  onClose, 
  response 
}: ResponseViewModalProps) {
  const { toast } = useSimpleToast();
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const copyToClipboard = async (text: string, section: string) => {
    try {
      setError(null);
      await navigator.clipboard.writeText(text);
      setCopiedSection(section);
      toast({
        title: "Copied to clipboard",
        description: `${section} copied successfully`,
      });
      
      // Clear the copied indicator after 2 seconds
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to copy to clipboard';
      setError(errorMessage);
      console.warn('Failed to copy to clipboard:', err);
      toast({
        title: "Copy failed",
        description: errorMessage,
      });
    }
  };

  const getProviderDisplayName = (provider: string) => {
    switch (provider) {
      case 'gemini':
        return 'Google Gemini';
      case 'openai':
        return 'OpenAI';
      case 'openrouter':
        return 'OpenRouter';
      default:
        return provider;
    }
  };

  const getRequestTypeDisplayName = (type: string) => {
    switch (type) {
      case 'generate':
        return 'Character Generation';
      case 'rewrite':
        return 'Field Rewrite';
      case 'chat-export':
        return 'Chat Export';
      default:
        return type;
    }
  };

  const formatDuration = (duration: number) => {
    if (duration < 1000) {
      return `${duration}ms`;
    }
    return `${(duration / 1000).toFixed(2)}s`;
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatResponseData = (data: any) => {
    try {
      if (typeof data === 'string') {
        return data;
      }
      return JSON.stringify(data, null, 2);
    } catch (err) {
      console.warn('Failed to format response data:', err);
      return 'Error: Unable to format response data';
    }
  };

  if (!response) return null;

  const hasError = !!response.error;
  const responseText = formatResponseData(response.data);
  const meta: any = response.metadata || {};
  const previousTags: string[] | undefined = Array.isArray(meta.previousTags) ? meta.previousTags : undefined;
  const updatedTags: string[] | undefined = Array.isArray(meta.updatedTags) ? meta.updatedTags : undefined;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {hasError ? (
              <>
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-300" />
                Debug: AI Response Error
              </>
            ) : (
              <>
                <Zap className="h-5 w-5 text-green-600" />
                Debug: AI Response
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-medium">Debug Error</div>
                <div className="text-sm mt-1">{error}</div>
              </AlertDescription>
            </Alert>
          )}

          {/* Response Metadata */}
          <div className="bg-muted/50 p-3 rounded-lg space-y-3">
            <div className="flex flex-wrap gap-2 text-sm">
              <Badge variant="outline">
                {getProviderDisplayName(response.provider)}
              </Badge>
              <Badge variant="outline">
                {response.model}
              </Badge>
              <Badge variant="outline">
                {getRequestTypeDisplayName(response.metadata.requestType)}
              </Badge>
              <Badge variant="outline" className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDuration(response.metadata.duration)}
              </Badge>
              {(response as any).metadata?.worldDocument && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:border-blue-800">
                  World: {(response as any).metadata.worldDocument.filename}
                </Badge>
              )}
            </div>
            
            <div className="text-xs text-muted-foreground">
              <div>Request Time: {formatTimestamp(response.metadata.timestamp)}</div>
            </div>
            
            {/* World Document Details */}
            {(response as any).metadata?.worldDocument && (
              <div className="p-3 bg-blue-50/50 rounded border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
                <div className="text-sm font-medium text-blue-800 dark:text-blue-100 mb-2">World Document Used</div>
                <div className="grid grid-cols-2 gap-2 text-xs text-blue-700 dark:text-blue-300">
                  <div><span className="font-medium">File:</span> {(response as any).metadata.worldDocument.filename}</div>
                  <div><span className="font-medium">Format:</span> {(response as any).metadata.worldDocument.format.toUpperCase()}</div>
                  <div><span className="font-medium">Size:</span> {Math.round((response as any).metadata.worldDocument.size / 1024)}KB</div>
                  <div><span className="font-medium">Method:</span> {(response as any).metadata.worldDocument.extractionMethod}</div>
                </div>
                <div className="mt-2 text-xs text-blue-600 dark:text-blue-300">
                  This response was generated with world/lore context from the uploaded document.
                </div>
              </div>
            )}
          </div>

          {/* Error Display */}
          {hasError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="space-y-2">
                <div className="font-medium">Request Failed</div>
                <div className="font-mono text-sm p-2 rounded border bg-red-50 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-100 dark:border-red-800">
                  {response.error}
                </div>
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(response.error || '', 'Error details')}
                    className="h-6 px-2"
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    {copiedSection === 'Error details' ? 'Copied!' : 'Copy Error'}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Response Data */}
          {!hasError && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Raw Response Data</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(responseText, 'Response data')}
                  className="h-6 px-2"
                >
                  <Copy className="h-3 w-3 mr-1" />
                  {copiedSection === 'Response data' ? 'Copied!' : 'Copy Response'}
                </Button>
              </div>
              
              <ScrollArea className="h-[300px] w-full border rounded-md">
                <pre className="p-4 text-xs font-mono whitespace-pre-wrap break-words">
                  {responseText}
                </pre>
              </ScrollArea>
            </div>
          )}

          {/* Chat Export Tags Before/After (AI Edit) */}
          {!hasError && (previousTags || updatedTags) && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Chat Export Tags (before → after)</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-muted/30 p-3 rounded">
                  <div className="text-xs text-muted-foreground mb-2">Before</div>
                  <div className="flex flex-wrap gap-1">
                    {previousTags && previousTags.length > 0 ? (
                      previousTags.map((t, idx) => (
                        <Badge key={`prev-${idx}`} variant="outline">{t}</Badge>
                      ))
                    ) : (
                      <span className="text-xs text-muted-foreground">None</span>
                    )}
                  </div>
                </div>
                <div className="bg-muted/30 p-3 rounded">
                  <div className="text-xs text-muted-foreground mb-2">After</div>
                  <div className="flex flex-wrap gap-1">
                    {updatedTags && updatedTags.length > 0 ? (
                      updatedTags.map((t, idx) => (
                        <Badge key={`next-${idx}`} variant="outline">{t}</Badge>
                      ))
                    ) : (
                      <span className="text-xs text-muted-foreground">None</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Additional Metadata */}
          <Separator />
          
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Response Metadata</h3>
            <div className="bg-muted/30 p-3 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="font-medium">Provider:</span> {getProviderDisplayName(response.provider)}
                </div>
                <div>
                  <span className="font-medium">Model:</span> {response.model}
                </div>
                <div>
                  <span className="font-medium">Request Type:</span> {getRequestTypeDisplayName(response.metadata.requestType)}
                </div>
                <div>
                  <span className="font-medium">Duration:</span> {formatDuration(response.metadata.duration)}
                </div>
                <div className="md:col-span-2">
                  <span className="font-medium">Timestamp:</span> {formatTimestamp(response.metadata.timestamp)}
                </div>
              </div>
              
              <div className="flex justify-end mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(JSON.stringify(response.metadata, null, 2), 'Metadata')}
                  className="h-6 px-2"
                >
                  <Copy className="h-3 w-3 mr-1" />
                  {copiedSection === 'Metadata' ? 'Copied!' : 'Copy Metadata'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button
            onClick={onClose}
            className="flex items-center gap-2"
          >
            <X className="h-4 w-4" />
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
